import type { CityConfig } from "@/pages/city-template/types";

export const newportConfig: CityConfig = {
  city: "Newport",
  state: "Rhode Island",
  stateAbbr: "RI",
  slug: "newport",
  route: "/cities/newport-ri",
  title: "Emergency & Expedited Passport Newport, RI | Cruise & Travel Passport Services",
  metaDescription:
    "Emergency passport Newport RI — 24-hour, 3-5 day, and same-day expedited passport processing for cruise travelers and international tourists. Fast Passport Center serves Newport, Middletown, Jamestown, and Aquidneck Island. Avoid the 8-11 week delay.",
  h1: "Emergency & Expedited Passport Services in Newport, RI",
  heroSubtitle:
    "Newport is America's yachting capital — a world-class destination for cruises, international sailing, and luxury travel. Fast Passport Center delivers emergency passports as fast as 24 hours, with 3-5 day expedited service for Newport, Middletown, Jamestown, and all of Aquidneck Island.",
  heroImageUrl:
    "https://readdy.ai/api/search-image?query=Iconic%20Newport%20Rhode%20Island%20harbor%20with%20historic%20mansions%2C%20yachts%20and%20sailboats%20on%20calm%20water%2C%20summer%20coastal%20scenery%20with%20blue%20sky%20and%20white%20clouds%2C%20elegant%20New%20England%20seaside%20resort%20town%2C%20professional%20travel%20photography%2C%20no%20text%20overlay&width=1600&height=700&seq=ri-newport-hero-001&orientation=landscape",
  heroImageAlt: "Newport Rhode Island harbor with historic mansions and yachts",
  geoPosition: "41.4901; -71.3128",
  geoPlacename: "Newport, Rhode Island, United States",
  lastModified: "2026-05-31",
  regionalAgency: {
    name: "Boston Passport Agency",
    address: "5 Thomas P. O'Neill Jr. Federal Building, 10 Causeway Street, Boston, MA 02222",
    distance: "~70 miles from Newport",
  },
  nearbyCitiesZips: [
    { name: "Newport", zips: "02840-02841" },
    { name: "Middletown", zips: "02842" },
    { name: "Jamestown", zips: "02835" },
    { name: "Portsmouth", zips: "02871" },
    { name: "Tiverton", zips: "02878" },
    { name: "Little Compton", zips: "02837" },
    { name: "Barrington", zips: "02806" },
    { name: "Bristol", zips: "02809" },
    { name: "Warren", zips: "02885" },
    { name: "Providence", zips: "02901-02912, 02918, 02940" },
    { name: "Warwick", zips: "02886-02889, 02818" },
    { name: "Cranston", zips: "02905-02907, 02910, 02920-02921" },
    { name: "East Greenwich", zips: "02818" },
    { name: "North Kingstown", zips: "02852, 02874" },
    { name: "Narragansett", zips: "02882" },
    { name: "South Kingstown", zips: "02879, 02881, 02883" },
    { name: "Westerly", zips: "02891" },
    { name: "Block Island", zips: "02807" },
  ],
  benefits: [
    {
      icon: "ri-time-line",
      title: "As Fast as 24 Hours",
      desc: "Emergency passport processing for Newport cruise travelers and international tourists with departures within 24 hours to 2 weeks.",
    },
    {
      icon: "ri-shield-check-line",
      title: "Registered Courier",
      desc: "U.S. Department of State registered passport courier with reserved agency submission slots.",
    },
    {
      icon: "ri-customer-service-2-line",
      title: "Real Human Support",
      desc: "Dedicated agents guide you through every step — no chatbots, no automated runaround.",
    },
    {
      icon: "ri-map-pin-line",
      title: "Serving Aquidneck Island",
      desc: "Newport, Middletown, Jamestown, Portsmouth — we cover every community on Aquidneck and Conanicut Islands.",
    },
  ],
  testimonials: [
    {
      name: "Elizabeth W.",
      location: "Newport, RI",
      rating: 5,
      text: "I booked a Mediterranean cruise departing from Newport and realized my passport expired 3 weeks before sailing. Fast Passport Center saved the trip with their 5-day expedited service. Absolutely stress-free.",
      service: "Passport Renewal",
    },
    {
      name: "James P.",
      location: "Middletown, RI",
      rating: 5,
      text: "Needed an emergency passport for a business trip to London. Fast Passport Center processed it in 3 days. The agent even helped me understand the visa requirements for the UK.",
      service: "Emergency Passport",
    },
    {
      name: "Margaret C.",
      location: "Jamestown, RI",
      rating: 5,
      text: "First-time passport for my grandchild before a family cruise. Fast Passport Center made the two-parent consent process simple and had the passport back in 5 days.",
      service: "Child Passport",
    },
  ],
  facilities: [
    {
      id: "newport-1",
      name: "Newport Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "50 Washington Square",
      city: "Newport",
      state: "RI",
      zip: "02840",
      phone: "401-846-9300",
      hours: "Mon-Fri 8:00 AM - 4:30 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Downtown Newport",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport acceptance services available by appointment. First-time applicants and those not eligible for mail-in renewal must apply in person.",
      icon: "ri-building-2-line",
    },
    {
      id: "newport-2",
      name: "Middletown Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "788 Aquidneck Avenue",
      city: "Middletown",
      state: "RI",
      zip: "02842",
      phone: "401-846-1800",
      hours: "Mon-Fri 8:00 AM - 4:30 PM",
      distance: "Middletown, near Newport",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport acceptance services available by appointment. Photo services available on-site.",
      icon: "ri-building-2-line",
    },
    {
      id: "newport-3",
      name: "Newport City Clerk",
      type: "City Clerk / Document Services",
      address: "43 Broadway",
      city: "Newport",
      state: "RI",
      zip: "02840",
      phone: "401-845-5380",
      hours: "Mon-Fri 8:30 AM - 4:30 PM",
      distance: "Downtown Newport",
      website: "https://www.cityofnewport.com/",
      websiteLabel: "City of Newport",
      notes: "Provides document and vital records services. For passport applications, visit an authorized post office or acceptance facility.",
      icon: "ri-government-line",
    },
  ],
  faqs: [
    {
      q: "How fast can I get an emergency passport in Newport, RI before a cruise?",
      a: "Fast Passport Center offers same-day emergency passport processing in as little as 24 hours for Newport residents with documented life-or-death emergencies. For cruise departures within 2 weeks, our 3-5 day expedited service is the most popular option. We frequently serve Newport cruise travelers who need passports fast — don't let an expired passport cancel your cruise.",
    },
    {
      q: "Where is the closest passport acceptance facility in Newport, RI?",
      a: "The Newport Post Office at 50 Washington Square and the Middletown Post Office at 788 Aquidneck Avenue both offer passport acceptance services by appointment. First-time applicants and those not eligible for mail-in renewal must apply in person at these facilities. Fast Passport Center guides Newport and Aquidneck Island residents through the acceptance clerk process.",
    },
    {
      q: "Can I get same-day passport photos in Newport, RI?",
      a: "Yes — passport photos are available at CVS Pharmacy, Walgreens, and UPS Store locations throughout Newport and Middletown. We also offer photo review as part of our service to ensure your 2x2 photos meet all State Department requirements before submission — critical for cruise travelers who can't afford delays.",
    },
    {
      q: "How much does an expedited passport cost in Newport for a cruise?",
      a: "Government fees are set by the State Department and vary based on passport type (book vs card, large book option). Fast Passport Center's service fees depend on processing speed: 10-day Smart Saver, 3-5 day Expedited, or 24-hour Emergency. For cruise travelers, we recommend the passport book (not card) as most cruise lines and international ports require it. Call 877-253-0084 for a personalized quote.",
    },
    {
      q: "Do I need to drive to the Boston Passport Agency from Newport?",
      a: "No — if you use Fast Passport Center, you do not need to visit the Boston Passport Agency at all. As a registered courier, we handle the agency submission for you. The only reason to visit the Boston agency directly is for a documented life-or-death emergency, and appointments are extremely difficult to obtain. Newport travelers especially benefit from avoiding the 70-mile drive to Boston.",
    },
    {
      q: "What is the fastest way to renew a passport in Newport, Rhode Island?",
      a: "The fastest way to renew a passport in Newport is through Fast Passport Center's 3-5 day expedited service. If your most recent passport was issued when you were 16 or older, within the last 15 years, and is undamaged, you may qualify for renewal without an acceptance clerk visit. For even faster renewal, our 24-hour emergency service is available.",
    },
    {
      q: "Can I get a child passport expedited in Newport, RI for a family cruise?",
      a: "Yes — Fast Passport Center expedites child passports for minors under 16 in Newport, Middletown, Jamestown, and all of Aquidneck Island. Both parents must appear with the child or provide notarized consent (Form DS-3053). We review all parental consent documentation before submission to avoid delays — critical for family cruises with fixed departure dates.",
    },
    {
      q: "Does Fast Passport Center have an office in Newport, RI?",
      a: "Fast Passport Center operates as a remote courier service — we do not have a physical office in Newport. We serve Newport, Middletown, Jamestown, and all of Rhode Island through our secure overnight shipping and agency submission process. Our headquarters are in Washington, DC.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Newport", address: "various CVS locations in Newport and Middletown", hours: "Mon-Sun, hours vary by location" },
    { name: "Walgreens — Newport", address: "various Walgreens locations in Newport area", hours: "Mon-Sun, hours vary by location" },
    { name: "UPS Store — Newport", address: "various UPS Store locations in Newport", hours: "Mon-Fri 9:00 AM - 7:00 PM, Sat 10:00 AM - 4:00 PM" },
    { name: "FedEx Office — Newport", address: "various FedEx Office locations near downtown Newport", hours: "Mon-Fri 8:00 AM - 9:00 PM, Sat-Sun 10:00 AM - 6:00 PM" },
    { name: "Walmart — Newport", address: "various Walmart locations near Newport and Middletown", hours: "Mon-Sun 7:00 AM - 10:00 PM" },
  ],
  dmvLocations: [
    {
      name: "RI DMV — Cranston",
      address: "600 New London Ave, Cranston, RI 02920",
      hours: "Mon-Fri 8:30 AM - 3:15 PM",
      note: "Main Rhode Island DMV office. Accepts walk-ins but appointments are strongly recommended. REAL ID and driver's license services.",
      featured: true,
    },
    {
      name: "RI DMV Express — Newport",
      address: "RI DMV Express services available in nearby Cranston",
      hours: "Mon-Fri 8:30 AM - 3:15 PM",
      note: "Newport residents typically use the Cranston DMV or Express locations for REAL ID and license services.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "PVD",
      name: "T.F. Green Airport",
      distance: "~30 miles from Newport",
      address: "2000 Post Rd, Warwick, RI 02886",
      note: "Rhode Island's primary airport, about 30 minutes from Newport. Serves domestic destinations and limited international connections. Most Newport cruise and international travelers connect through here.",
      flightUrl: "https://www.pvdairport.com/",
      flightLabel: "T.F. Green Airport",
      featured: true,
    },
    {
      code: "BOS",
      name: "Logan International Airport",
      distance: "~70 miles from Newport",
      address: "1 Harborside Dr, Boston, MA 02128",
      note: "Major international hub with direct flights to Europe, the Caribbean, and worldwide destinations. Newport travelers often use Logan for international travel and cruise connections.",
      flightUrl: "https://www.massport.com/logan-airport",
      flightLabel: "Logan International",
      featured: false,
    },
    {
      code: "BDL",
      name: "Bradley International Airport",
      distance: "~90 miles from Newport",
      address: "Schoephoester Rd, Windsor Locks, CT 06096",
      note: "Connecticut's major airport with domestic and some international service. Alternative option for Newport-area travelers.",
      flightUrl: "https://www.bradleyairport.com/",
      flightLabel: "Bradley International",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-passport-line",
      title: "Emergency Passport",
      description: "For Newport cruise and international travelers with urgent departures within 24 hours to 2 weeks. Reserved agency submission with dedicated courier handling.",
      bullets: ["Processing as fast as 24 hours", "Dedicated agent assigned to your case", "Daily status updates"],
      badge: "Fastest",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Fast renewal for Newport residents with a valid passport issued within the last 15 years at age 16 or older.",
      bullets: ["3-5 day or 10-day options available", "No acceptance clerk visit required", "Secure overnight document return"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-user-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for Newport residents with step-by-step guidance through the acceptance clerk process.",
      bullets: ["Complete application review before submission", "Acceptance clerk appointment guidance", "3-5 day or 10-day processing options"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alarm-warning-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement service for Newport residents who have lost their passport or had it stolen before travel.",
      bullets: ["DS-64 form guidance included", "Replacement in 3-5 days or 10 days", "Acceptance clerk visit required"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 in Newport, with parental consent guidance and acceptance clerk support.",
      bullets: ["Parental consent documentation review", "Both parents present or notarized consent", "3-5 day or 10-day processing available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal name change for Newport residents.",
      bullets: ["Marriage certificate or court order review", "Name change in 3-5 days or 10 days", "No clerk visit for most renewals"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Newport travelers with immediate departure needs." },
    { name: "Passport Renewal", description: "Fast passport renewal for Newport residents with a recent valid passport." },
    { name: "New Passport", description: "First-time passport application assistance for Newport residents." },
    { name: "Lost or Stolen Passport", description: "Passport replacement for Newport residents who have lost their passport." },
    { name: "Child Passport", description: "Passport services for minors under 16 in Newport." },
    { name: "Name Change", description: "Passport name change service after marriage, divorce, or legal name change in Newport." },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Photos must be 2\"x2\" with a plain white or off-white background. Many Newport cruise travelers get rejected at the acceptance clerk because their photos don't meet strict State Department specs.",
    },
    {
      icon: "ri-file-list-3-line",
      title: "Missing or Incorrect Forms",
      description: "Using the wrong form (DS-11 vs DS-82) or missing required fields can cause weeks of delays. We review every form before submission.",
    },
    {
      icon: "ri-calendar-line",
      title: "Waiting Too Long to Start",
      description: "Standard processing takes 8-11 weeks. Many Newport travelers with cruise bookings wait until the last minute, leaving no buffer for problems.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Incorrect Payment Amount",
      description: "Government fees change frequently. Using an outdated check or wrong amount will cause your application to be returned.",
    },
    {
      icon: "ri-shield-line",
      title: "Damaged or Expired ID",
      description: "Your driver's license or state ID must be valid and unexpired. Many Newport applicants don't realize their ID is about to expire until it's too late.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For child passports, both parents must appear or provide notarized consent. Missing documentation is the #1 reason child applications are delayed — especially for family cruises.",
    },
  ],
  steps: [
    {
      number: "01",
      icon: "ri-file-list-3-line",
      title: "Complete Your Application",
      description: "Fill out your passport application online or over the phone with our Newport-area team. We review everything for accuracy.",
    },
    {
      number: "02",
      icon: "ri-upload-cloud-line",
      title: "Send Us Your Documents",
      description: "Ship your documents via overnight courier to our secure processing center. We confirm receipt and begin review immediately.",
    },
    {
      number: "03",
      icon: "ri-search-line",
      title: "We Review Everything",
      description: "Our agents verify your forms, photos, and supporting documents meet all State Department requirements — no surprises.",
    },
    {
      number: "04",
      icon: "ri-shield-check-line",
      title: "Courier Submission",
      description: "As a registered U.S. Department of State courier, we submit your application with reserved agency processing slots.",
    },
    {
      number: "05",
      icon: "ri-passport-line",
      title: "Receive Your Passport",
      description: "Your passport is delivered securely to your Newport address. We provide tracking and daily status updates throughout.",
    },
  ],
  processingOptions: [
    {
      speed: "8-11",
      unit: "weeks",
      label: "Standard Processing",
      tag: "Post Office",
      tagColor: "bg-gray-200 text-gray-600",
      features: [
        "Mail-in application",
        "No expedited handling",
        "No dedicated agent",
        "No phone or email contact for your case",
        "Updates only by physical mail letter",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "10",
      unit: "business days",
      label: "Smart Saver",
      tag: "Fast Passport Center",
      tagColor: "bg-green-100 text-green-700",
      features: [
        "Secure overnight shipping included",
        "Document review before submission",
        "Dedicated agent assigned",
        "Daily status updates",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "3-5",
      unit: "business days",
      label: "Expedited Processing",
      tag: "Recommended",
      tagColor: "bg-[#1A365D] text-white",
      features: [
        "Secure overnight shipping included",
        "Document review before submission",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "24",
      unit: "hours",
      label: "Emergency Processing",
      tag: "Life or Death",
      tagColor: "bg-red-100 text-red-600",
      features: [
        "For documented life-or-death emergencies only",
        "Requires proof of emergency travel",
        "Strictly for documented life-or-death emergencies",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Must book weeks in advance", fpc: "No appointment needed — we handle agency access" },
    { feature: "Processing Speed", agency: "1-2 business days (if you get an appointment)", fpc: "As fast as 3-5 days or 24 hours for emergencies" },
    { feature: "Document Review", agency: "No pre-check — one mistake means restart", fpc: "Complete review before submission — we catch errors" },
    { feature: "Travel Proof", agency: "Requires printed itinerary with confirmed travel within 2 weeks", fpc: "Flexible documentation requirements" },
    { feature: "Updates", agency: "No phone or email support — only mail updates", fpc: "Daily status updates via phone and email" },
    { feature: "Guarantee", agency: "No guarantee of appointment or same-day processing", fpc: "Reserved agency slots with predictable processing" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Providence", route: "/cities/providence" },
    { name: "Warwick", route: "/cities/warwick" },
    { name: "Cranston", route: "/cities/cranston" },
    { name: "Pawtucket", route: "/cities/pawtucket" },
    { name: "Middletown", route: "/states/rhode-island" },
    { name: "Jamestown", route: "/states/rhode-island" },
    { name: "Portsmouth", route: "/states/rhode-island" },
    { name: "Tiverton", route: "/states/rhode-island" },
    { name: "East Greenwich", route: "/states/rhode-island" },
    { name: "Narragansett", route: "/states/rhode-island" },
  ],
  fpcOfficeNote:
    "Fast Passport Center serves Newport, Middletown, Jamestown, and all of Aquidneck Island as a remote courier service. We do not have a physical office in Newport, but our secure overnight shipping network ensures your documents reach our processing center quickly and safely.",
  fpcOfficeDistance: "Remote courier service — Newport, RI area",
  serviceAreaIntro:
    "Fast Passport Center provides expedited passport services to all Newport neighborhoods and surrounding Aquidneck Island communities. From the historic mansions to the yachting harbor, Ocean Drive to Bellevue Avenue — we serve every Newport area with the same fast, reliable processing.",
  nearbyCitiesIntro:
    "We proudly serve Newport, Middletown, Jamestown, Portsmouth, and all of Aquidneck and Conanicut Islands. Whether you're preparing for a cruise, international sailing, or luxury travel, our expedited passport services are available with the same speed and reliability.",
  howItWorksIntro:
    "Our Newport customers follow a simple, proven process to get their passports fast. No agency visits, no waiting in lines, no guessing — so you can focus on your cruise or travel plans.",
  mistakesIntro:
    "These are the most common mistakes Newport travelers make when applying for an expedited passport — especially for cruise and international travel. Avoiding them saves weeks of delays.",
  testimonialsIntro:
    "Newport, Middletown, and Jamestown travelers trust Fast Passport Center for their urgent passport and cruise travel needs. Here's what they say about our service.",
  faqIntro:
    "Common questions about emergency and expedited passport services in Newport, RI — especially for cruise travelers and international tourists. If you don't see your question here, call us at 877-253-0084.",
  statePageRoute: "/states/rhode-island",
  statePageName: "Rhode Island Passport Services",
};

export default newportConfig;